import { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../AuthContext';
import { useVersionedApi } from '../utils/versionedApi';
import { apiLog } from '../utils/debugConfig';

import { safeStorage } from '../utils/safeStorage';
import { isRestrictedAccount } from '../utils/accountKind';
const AccessControlContext = createContext(null);

export function AccessControlProvider({ children }) {
  const { user } = useAuth();
  const apiClient = useVersionedApi('v1');
  const [accessConfig, setAccessConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = safeStorage.getItem('evv_token');
    if (!user || !token) {
      setAccessConfig(null);
      setLoading(false);
      return;
    }

    // /access-control/me resolves rules within a tenant. Platform admins have no
    // organizationId, so the call 401s — give them full access locally instead.
    if (!user.organizationId || isRestrictedAccount(user)) {
      setAccessConfig({
        rules: [],
        allowedSections: [],
        userContext: { role: user.role, providerType: user.providerType, permissions: [] }
      });
      setLoading(false);
      return;
    }

    const loadAccessConfig = async () => {
      try {
        const subdomain = safeStorage.getItem('organization_subdomain');

        const response = await apiClient.get('/access-control/me', {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-organization-subdomain': subdomain
          }
        });

        apiLog('🔐 Access config loaded:', response.data);
        setAccessConfig(response.data);
      } catch (error) {
        console.error('Failed to load access configuration:', error);
        // Set default minimal access if loading fails
        setAccessConfig({
          rules: [],
          allowedSections: ['dashboard', 'patients'],
          userContext: {
            role: user?.role,
            providerType: user?.providerType,
            permissions: []
          }
        });
      } finally {
        setLoading(false);
      }
    };

    loadAccessConfig();
  }, [user, apiClient]);

  const filterResources = (resourceType, resources) => {
    if (!accessConfig || !resources) {
      return resources;
    }

    // Super admin bypass - see all resources
    if (user?.role === 'super_admin') {
      apiLog(`👑 Super admin bypass: showing all ${resources.length} ${resourceType}`);
      return resources;
    }

    // Special handling for programs - check user assignments first
    if (resourceType === 'programs' && accessConfig.userContext?.assignedPrograms?.length > 0) {
      const assignedProgramIds = accessConfig.userContext.assignedPrograms.map(p => p.id);
      apiLog(`🔐 User has ${assignedProgramIds.length} assigned programs:`, accessConfig.userContext.assignedPrograms.map(p => p.name));

      const filtered = resources.filter(resource =>
        assignedProgramIds.includes(resource.id)
      );

      apiLog(`🔍 Filtered ${resources.length} programs to ${filtered.length} based on assignments`);
      return filtered;
    }

    // Fall back to rule-based filtering
    if (!accessConfig.rules) {
      return resources;
    }

    const applicableRules = accessConfig.rules
      .filter(r => r.resourceType === resourceType && r.accessType === 'filter')
      .sort((a, b) => b.priority - a.priority);

    if (applicableRules.length === 0) {
      return resources;
    }

    const primaryRule = applicableRules[0];

    if (!primaryRule.filterConfig) {
      return resources; // No filter = see all
    }

    apiLog(`🔍 Applying filter rule "${primaryRule.name}" to ${resources.length} ${resourceType}`);

    return resources.filter(resource => {
      const nameCheck = (resource.name || '').toLowerCase();

      // Check include patterns
      if (primaryRule.filterConfig.includePatterns?.length > 0) {
        const matches = primaryRule.filterConfig.includePatterns.some(pattern =>
          nameCheck.includes(pattern.toLowerCase())
        );
        if (!matches) {
          apiLog(`  ❌ Filtered out: ${resource.name} (no include pattern match)`);
          return false;
        }
      }

      // Check exclude patterns
      if (primaryRule.filterConfig.excludePatterns?.length > 0) {
        const excluded = primaryRule.filterConfig.excludePatterns.some(pattern =>
          nameCheck.includes(pattern.toLowerCase())
        );
        if (excluded) {
          apiLog(`  ❌ Filtered out: ${resource.name} (exclude pattern match)`);
          return false;
        }
      }

      apiLog(`  ✅ Allowed: ${resource.name}`);
      return true;
    });
  };

  const canAccessSection = (sectionName) => {
    if (!accessConfig) return false;

    // Super admin bypass
    if (user?.role === 'super_admin') return true;

    const allowed = accessConfig.allowedSections?.includes(sectionName);
    apiLog(`🔐 Section access check: ${sectionName} = ${allowed ? 'allowed' : 'denied'}`);
    return allowed;
  };

  const canAccessRoute = (route) => {
    if (!accessConfig || !accessConfig.rules) return true;

    // Super admin bypass
    if (user?.role === 'super_admin') return true;

    const routeRules = accessConfig.rules.filter(r =>
      r.resourceType === 'routes' && r.accessType === 'view'
    );

    if (routeRules.length === 0) return true;

    const canAccess = routeRules.some(rule => {
      if (!rule.filterConfig?.allowedRoutes) return true;

      return rule.filterConfig.allowedRoutes.some(pattern => {
        const regexPattern = pattern.replace(/\*/g, '.*');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(route);
      });
    });

    apiLog(`🔐 Route access check: ${route} = ${canAccess ? 'allowed' : 'denied'}`);
    return canAccess;
  };

  const hasResourceAccess = (resourceType, accessType = 'view') => {
    if (!accessConfig || !accessConfig.rules) return false;

    // Super admin bypass
    if (user?.role === 'super_admin') return true;

    return accessConfig.rules.some(rule =>
      rule.resourceType === resourceType &&
      rule.accessType === accessType
    );
  };

  return (
    <AccessControlContext.Provider
      value={{
        accessConfig,
        loading,
        filterResources,
        canAccessSection,
        canAccessRoute,
        hasResourceAccess,
        userRole: user?.role,
        providerType: user?.providerType
      }}
    >
      {children}
    </AccessControlContext.Provider>
  );
}

export function useAccessControl() {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within AccessControlProvider');
  }
  return context;
}